import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MdApartment, MdOpenInNew } from 'react-icons/md';
import { differenceInCalendarDays } from 'date-fns';
import clsx from 'clsx';
import { Link } from '@tanstack/react-router';
import type { AgentApiJobChangeProcessesRecord } from '@/types/serializers';
import { Button } from '@/components/Button';
import { Tag } from '@/components/Tag';
import useFetchSelectionReminders from '../api/useFetchSelectionReminders';
import DashboardSelectionReminderActions from './DashboardSelectionReminderActions';
import DashboardEmptyState from './DashboardEmptyState';

/** 進捗（current_progress）の日本語ラベル */
const PROGRESS_LABEL: Record<AgentApiJobChangeProcessesRecord['current_progress'], string> = {
  none: '未着手',
  document_screening: '書類選考',
  meeting: 'CA面談',
  '1st_interview': '一次選考',
  '2nd_interview': '二次選考',
  '3rd_interview': '三次選考',
  '4th_interview': '四次選考',
  final_interview: '最終選考',
  offer_meeting: 'オファー面談',
  offered: '内定',
  closed: '決定',
};

/** 経過日数を返す（0 以下は 0） */
function elapsedDays(lastActedAt: string): number {
  const last = new Date(lastActedAt);
  if (Number.isNaN(last.getTime())) return 0;
  const d = differenceInCalendarDays(new Date(), last);
  return d < 0 ? 0 : d;
}

/** ステータス文言（result_waiting / adjust_scheduling 用） */
function statusText(item: AgentApiJobChangeProcessesRecord): string {
  const progress = PROGRESS_LABEL[item.current_progress] ?? item.progress_name;
  switch (item.current_schedule_status) {
    case 'result_waiting':
      return `${progress}結果待ち`;
    case 'adjust_scheduling':
      return `${progress}日程調整中`;
    case 'scheduled':
      return `${progress}日程確定`;
    case 'none':
    default:
      return progress;
  }
}

/** 経過日数タグ（7日以上は赤、それ未満はグレー。Figma StatusTag h-24 px-12 rounded-4） */
function ElapsedTag({ days }: { days: number }) {
  const isHigh = days >= 7;
  return (
    <span
      className={clsx(
        'inline-flex h-6 shrink-0 items-center justify-center rounded-sm px-sm text-xs leading-none font-bold whitespace-nowrap',
        isHigh ? 'bg-danger_lighten text-danger' : 'bg-tag-gray text-tag-gray'
      )}
    >
      {days === 0 ? '本日' : `${days}日経過`}
    </span>
  );
}

type DashboardSelectionRemindersProps = {
  /** 担当者で絞り込み（任意） */
  organizationUserId?: string;
};

/**
 * 未対応の選考セクション（Figma 4717:18150）。
 *
 * - ヘッダ: 「未対応の選考」 + 件数 Tag + 「もっと見る」リンクボタン
 * - 各行: 候補者・企業 / ステータス変更メニュー（+ 日程調整中は時計ボタン）/ 経過日数タグ
 * - スクロール表示（ページャなし）
 */
export default function DashboardSelectionReminders({
  organizationUserId,
}: DashboardSelectionRemindersProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useFetchSelectionReminders(1, organizationUserId);

  const items = useMemo(() => data?.records ?? [], [data]);

  // 選考を更新したら、一覧に加えてダッシュボード（本日の予定）も再取得する
  const handleRefetch = useCallback(
    () => Promise.all([refetch(), queryClient.invalidateQueries({ queryKey: ['dashboard'] })]),
    [refetch, queryClient]
  );

  return (
    <div className="flex h-full flex-col gap-xs rounded-sm bg-white px-md pt-xs pb-md shadow-[0_0_3px_0_rgba(0,0,0,0.2)] xl:min-h-0">
      {/* ヘッダ */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-sm">
          <h2 className="text-sm leading-normal font-bold text-[#101828]">未対応の選考</h2>
          {data && (
            <Tag color="blue" size="md" className="leading-none">
              {data.total_count.toLocaleString()}件
            </Tag>
          )}
        </div>
        <Button asChild color="tertiary" size="md">
          <Link to={'/agent/job_change_processes' as never}>
            <MdOpenInNew size={20} aria-hidden="true" />
            もっと見る
          </Link>
        </Button>
      </div>

      {/* 本体 */}
      <div className="hidden-scrollbar flex min-h-0 flex-1 flex-col gap-xs overflow-y-auto rounded-sm p-xxs">
        {isLoading ? (
          <ul className="flex flex-col gap-xs">
            {Array.from({ length: 7 }).map((_, i) => (
              <li
                key={i}
                className="flex h-[77px] items-center justify-between gap-md rounded-sm bg-white px-md shadow-[0_0_1px_0_rgba(0,0,0,0.2)]"
              >
                <div className="flex w-[180px] shrink-0 flex-col gap-xs">
                  <span className="h-[22px] w-[148px] animate-pulse rounded-sm bg-medium-gray" />
                  <span className="h-[15px] w-[148px] animate-pulse rounded-sm bg-medium-gray" />
                </div>
                <span className="h-[42px] w-[228px] shrink-0 animate-pulse rounded-sm bg-medium-gray" />
                <span className="h-5 w-[75px] shrink-0 animate-pulse rounded-sm bg-medium-gray" />
              </li>
            ))}
          </ul>
        ) : isError ? (
          <DashboardEmptyState className="min-h-[200px]" />
        ) : items.length === 0 ? (
          <DashboardEmptyState className="min-h-[200px]" />
        ) : (
          <ul className="flex flex-col gap-xs">
            {items.map((item) => {
              const days = elapsedDays(item.last_acted_at);
              return (
                <li
                  key={item.id}
                  className="flex h-[77px] items-center justify-between gap-md rounded-sm bg-white px-md shadow-[0_0_1px_0_rgba(0,0,0,0.2)]"
                >
                  {/* 左ブロック: 候補者・企業 (w-[180px]) */}
                  <div className="flex w-[180px] shrink-0 flex-col gap-xxs overflow-hidden">
                    <span className="truncate text-sm leading-normal font-bold text-main">
                      {item.candidate_profile.name}
                    </span>
                    <div className="flex min-w-0 items-center text-xs leading-[1.7] text-main">
                      <MdApartment size={12} className="shrink-0" aria-hidden="true" />
                      <span className="truncate">{item.company?.name ?? '—'}</span>
                    </div>
                  </div>

                  {/* 中央: ステータス変更メニュー + 日程調整ボタン */}
                  <DashboardSelectionReminderActions
                    jobChangeProcess={item}
                    statusLabel={statusText(item)}
                    refetch={handleRefetch}
                  />

                  {/* 経過日数 */}
                  <ElapsedTag days={days} />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
